import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { VerticalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { blockchainRarities } from '@sorare/core/src/lib/cards';

import { SelectRarity } from 'atoms/SelectRarity';

import { LazyCollectionElement } from '../LazyCollectionElement';
import type { CollectionUpsellContent_CardCollectionScoreIncrease } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0 })``;
const CollectionSelector = styled(Horizontal)`
  overflow-x: auto;
  padding: var(--unit) var(--double-unit);
  margin: var(--unit) 0;
`;

const Divider = styled(VerticalDivider)`
  margin: 0;
`;
const Button = styled.button`
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
  padding: var(--half-unit) var(--unit);
  cursor: pointer;
  &.selected {
    border: 1px solid transparent;

    background-color: var(--c-nd-150);
  }
`;

const ButtonLabel = styled(BodyM)`
  white-space: nowrap;
`;

type Props = {
  affectedCardCollections: CollectionUpsellContent_CardCollectionScoreIncrease[];
};

export const Content = ({ affectedCardCollections }: Props) => {
  const rarities: Rarity[] = [
    ...new Set(
      affectedCardCollections
        .map(
          affectedCardCollection =>
            affectedCardCollection.userCardCollection?.cardCollection.rarity
        )
        .filter(Boolean)
    ),
  ].sort(
    (a, b) =>
      (blockchainRarities.findIndex(r => r === a) || 0) -
      (blockchainRarities.findIndex(r => r === b) || 0)
  );
  const affectedCardCollectionsByRarity: Record<
    Rarity,
    CollectionUpsellContent_CardCollectionScoreIncrease[]
  > = affectedCardCollections.reduce(
    (acc, affectedCardCollection) => {
      const rarity =
        affectedCardCollection?.userCardCollection?.cardCollection?.rarity;
      if (!rarity) {
        return acc;
      }
      if (!acc[rarity]) {
        acc[rarity] = [];
      }
      acc[rarity].push(affectedCardCollection);
      return acc;
    },
    {} as Record<Rarity, CollectionUpsellContent_CardCollectionScoreIncrease[]>
  );

  const [rarity, setRarity] = useState<Rarity>(rarities[0]);
  const collections = affectedCardCollectionsByRarity[rarity];

  const [selectedCollection, setSelectedCollection] =
    useState<CollectionUpsellContent_CardCollectionScoreIncrease>(
      collections[0]
    );

  return (
    <Root>
      <CollectionSelector>
        {rarities?.length > 1 && (
          <>
            <SelectRarity
              selectedRarity={rarity}
              rarities={rarities}
              onChange={r => {
                setRarity(r);
                setSelectedCollection(affectedCardCollectionsByRarity[r][0]);
              }}
            />
            <Divider color="var(--c-nd-150)" />
          </>
        )}
        {collections?.map(affectedCardCollection => (
          <Button
            key={affectedCardCollection.scoreIncrease}
            className={classNames({
              selected:
                selectedCollection.userCardCollection?.id ===
                affectedCardCollection.userCardCollection?.id,
            })}
            onClick={() => setSelectedCollection(affectedCardCollection)}
          >
            <ButtonLabel as="span" bold>
              <TeamAvatar
                size={16}
                team={
                  affectedCardCollection.userCardCollection?.cardCollection
                    ?.anyTeam
                }
              />{' '}
              {
                affectedCardCollection.userCardCollection?.cardCollection
                  ?.anyTeam?.shortName
              }
            </ButtonLabel>
          </Button>
        ))}
      </CollectionSelector>
      <LazyCollectionElement cardCollectionScoreIncrease={selectedCollection} />
    </Root>
  );
};

Content.fragments = {
  cardCollectionScoreIncrease: gql`
    fragment CollectionUpsellContent_CardCollectionScoreIncrease on CardCollectionScoreIncrease {
      id
      scoreIncrease
      userCardCollection {
        id
        score
        bonus
        cardCollection {
          id
          slug
          rarity
          backgroundPictureUrl
          bannerPictureUrl
          anyTeam {
            slug
            shortName
            ...TeamAvatar_team
          }
        }
      }
      ...LazyCollectionElement_CardCollectionScoreIncrease
    }
    ${TeamAvatar.fragments.team}
    ${LazyCollectionElement.fragments.cardCollectionScoreIncrease}
  ` as TypedDocumentNode<CollectionUpsellContent_CardCollectionScoreIncrease>,
};
